import { useState } from 'react';
import { RadioGroup, FormControlLabel, Radio, IconButton } from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import ManagementConstants from '@/constants/ManagementConstants';
import { RTL } from 'arbitrip-common/translation/const';
import { ReactComponent as Calendar } from '@/img/bookings/calendar.svg';

const { ADAPTER_MOMENT_LOCALE_EN, ADAPTER_MOMENT_LOCALE_HE, DATE_PICKER_FORMAT } = ManagementConstants;

const MODES = {
	CANCELLATION_DEADLINE: 'cancellation_deadline',
	CHECK_IN: 'check_in',
};

const BookingsDateFilter = ({ filters = {}, initFilters = {}, setFilters = () => {} }) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.dir() === RTL;
	const adapterLocale = isRTL ? ADAPTER_MOMENT_LOCALE_HE : ADAPTER_MOMENT_LOCALE_EN;

	const getInitialMode = () => {
		// Default to cancellation deadline when no filters are selected.
		if (!filters?.start_date && !filters?.end_date) {
			return MODES.CANCELLATION_DEADLINE;
		}

		// If only check-in/out dates are set, switch to CHECK_IN mode
		if (filters?.start_date || filters?.end_date) {
			return MODES.CHECK_IN;
		}

		// Otherwise fall back to cancellation deadline
		return MODES.CANCELLATION_DEADLINE;
	};

	const [mode, setMode] = useState(getInitialMode);
	const [startOpen, setStartOpen] = useState(false);
	const [endOpen, setEndOpen] = useState(false);

	const handleModeChange = (e) => {
		const value = e.target.value;
		setMode(value);

		if (value === MODES.CHECK_IN) {
			// Clear cancellation deadline filters when switching to check-in mode
			setFilters({
				...filters,
				cancellation_deadline_start: initFilters.cancellation_deadline_start,
				cancellation_deadline_end: initFilters.cancellation_deadline_end,
			});
		} else {
			// Clear check-in filters when switching to cancellation deadline mode
			setFilters({
				...filters,
				start_date: initFilters.start_date,
				end_date: initFilters.end_date,
			});
		}
	};

	// === DATE VALUES & CHANGE HANDLERS === //
	const fromValue = mode === MODES.CANCELLATION_DEADLINE ? filters?.cancellation_deadline_start : filters?.start_date;
	const toValue = mode === MODES.CANCELLATION_DEADLINE ? filters?.cancellation_deadline_end : filters?.end_date;

	const onFromChange = (date) => {
		if (mode === MODES.CANCELLATION_DEADLINE) {
			setFilters({
				...filters,
				cancellation_deadline_start: date,
				// If start is after end, reset end
				...(toValue &&
					date &&
					date.isAfter(toValue) && {
						cancellation_deadline_end: initFilters.cancellation_deadline_end,
					}),
			});
		} else {
			setFilters({
				...filters,
				start_date: date,
				// If start is after end, reset end
				...(toValue && date && date.isAfter(toValue) && { end_date: initFilters.end_date }),
			});
		}
	};

	const onToChange = (date) => {
		if (mode === MODES.CANCELLATION_DEADLINE) {
			setFilters({
				...filters,
				cancellation_deadline_end: date,
			});
		} else {
			setFilters({
				...filters,
				end_date: date,
			});
		}
	};

	return (
		<div className="bookings-date-filter">
			<div className="bookings-date-filter__title">{t('Bookings.Filter.SearchByDate')}</div>

			<RadioGroup row value={mode} onChange={handleModeChange} className="bookings-date-filter__mode">
				<FormControlLabel
					value={MODES.CANCELLATION_DEADLINE}
					control={<Radio />}
					label={t('Bookings.Filter.CancellationDeadline')}
				/>
				<FormControlLabel value={MODES.CHECK_IN} control={<Radio />} label={t('Bookings.Filter.CheckInDate')} />
			</RadioGroup>

			<LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={adapterLocale}>
				<div className="bookings-date-filter__dates">
					<div className="bookings-date-filter__date">
						<label className="bookings-date-filter__picker-label">{t('Bookings.Filter.SelectDateFrom')}</label>
						<DesktopDatePicker
							format={DATE_PICKER_FORMAT}
							onClose={() => setStartOpen(false)}
							open={startOpen}
							className="desktop-date-picker"
							slots={{
								openPickerButton: () => (
									<IconButton>
										<Calendar />
									</IconButton>
								),
							}}
							slotProps={{
								textField: {
									InputProps: {
										onClick: (e) => {
											e.preventDefault();
											setStartOpen(true);
										},
										onKeyDown: (e) => {
											if (e.key !== 'Tab') {
												e.preventDefault();
												setStartOpen(true);
											}
										},
									},
								},
							}}
							value={fromValue}
							onChange={onFromChange}
						/>
					</div>
					<div className="bookings-date-filter__date">
						<label className="bookings-date-filter__picker-label">{t('Bookings.Filter.SelectDateTo')}</label>
						<DesktopDatePicker
							format={DATE_PICKER_FORMAT}
							onClose={() => setEndOpen(false)}
							open={endOpen}
							className="desktop-date-picker"
							slots={{
								openPickerButton: () => (
									<IconButton>
										<Calendar />
									</IconButton>
								),
							}}
							slotProps={{
								textField: {
									InputProps: {
										onClick: (e) => {
											e.preventDefault();
											setEndOpen(true);
										},
										onKeyDown: (e) => {
											if (e.key !== 'Tab') {
												e.preventDefault();
												setEndOpen(true);
											}
										},
									},
								},
							}}
							value={toValue}
							minDate={fromValue}
							onChange={onToChange}
						/>
					</div>
				</div>
			</LocalizationProvider>
		</div>
	);
};

export default BookingsDateFilter;
